import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Minus, Plus, Search, Users } from "lucide-react";
import { parseISO } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useLang } from "@/lib/i18n";
import { fmtDate, toISO } from "@/lib/api";

interface Props { initialGuests?: number; initialDate?: string; compact?: boolean }

export default function SearchBar({ initialGuests = 2, initialDate = "", compact = false }: Props) {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [guests, setGuests] = useState(initialGuests);
  const [date, setDate] = useState(initialDate);
  const [open, setOpen] = useState(false);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = new URLSearchParams({ guests: String(guests) });
    if (date) q.set("date", date);
    nav(`/yachts?${q.toString()}`);
  };

  const cell = "flex-1 min-w-0 px-5 py-4 text-left";
  return (
    <form onSubmit={submit} data-testid="hero-search-form"
      className={`w-full bg-white shadow-[0_30px_80px_-30px_rgba(11,25,44,0.5)] border-t-2 border-[#D4AF37] flex flex-col md:flex-row md:items-stretch ${compact ? "rounded-lg" : "rounded-xl"}`}>
      <div className={`${cell} border-b md:border-b-0 md:border-r border-slate-200`}>
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold"><Users className="h-3.5 w-3.5 text-[#00A8A0]" />{t("search.guests")}</span>
        <div className="mt-2 flex items-center gap-4">
          <button type="button" data-testid="hero-search-guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))}
            className="grid h-8 w-8 place-items-center rounded-full border border-slate-300 text-[#0B192C] transition-colors hover:border-[#00D2C8]"><Minus className="h-3.5 w-3.5" /></button>
          <span data-testid="hero-search-guests-value" className="font-display text-2xl text-[#0B192C] w-8 text-center">{guests}</span>
          <button type="button" data-testid="hero-search-guests-plus" onClick={() => setGuests((g) => Math.min(60, g + 1))}
            className="grid h-8 w-8 place-items-center rounded-full border border-slate-300 text-[#0B192C] transition-colors hover:border-[#00D2C8]"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="hero-search-date-picker" className={`${cell} border-b md:border-b-0 border-slate-200 hover:bg-slate-50 transition-colors`}>
            <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold"><CalendarDays className="h-3.5 w-3.5 text-[#00A8A0]" />{t("search.date")}</span>
            <span className={`mt-2 block font-display text-xl truncate ${date ? "text-[#0B192C]" : "text-slate-400"}`}>{date ? fmtDate(date, lang) : t("search.pickDate")}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={lang === "es" ? es : enUS} selected={date ? parseISO(date) : undefined} disabled={{ before: today }}
            onSelect={(d: Date | undefined) => { if (d) { setDate(toISO(d)); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
      <button type="submit" data-testid="hero-search-submit-btn"
        className="m-2 inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B192C] px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-[#13294a]">
        <Search className="h-4 w-4 text-[#00D2C8]" />{t("search.submit")}
      </button>
    </form>
  );
}
